import * as React from "react";
import styled from "@emotion/styled";
import { type To } from "react-router-dom";
import { Button, Container, FadeIn, IconMegaphone, IconX, Link } from "atoms";

type Announcement = {
  /**
   * Unique identifier for each announcement, can be simple incremented integer
   */
  id: number;
  /**
   * Main content of the announcement
   */
  message: React.ReactNode;
  /**
   * Optional link to show after message
   */
  link?: {
    text: React.ReactNode;
    to?: To;
    href?: string;
  };
};

const currentAnnouncements: Announcement[] = [
  {
    id: 1,
    message: (
      <span>
        <IconMegaphone style={{ marginRight: 3 }} /> You can now earn HUG Points
        for inviting artists to apply&nbsp;to&nbsp;HUG!
      </span>
    ),
    link: { text: "Get your invite link", to: "/artist-invites" },
  },
  // {
  //   id: 5,
  //   message: (
  //     <span>
  //       <IconMegaphone style={{ marginRight: 3 }} /> OPEN CALL: Showcase your
  //       photograph for World Photography Day in Bali, Indonesia!
  //     </span>
  //   ),
  //   link: {
  //     text: "Submit Now",
  //     href: "https://bit.ly/hugphotoday",
  //   },
  // },
];

// Local storage key for comma-separated list of announcement ids marked as read
const ANNOUNCEMENT_KEY = "HUG_announcements_read";
// Get list from storage and convert to array of numbers
const initialAnnouncementsRead = (
  window.localStorage.getItem(ANNOUNCEMENT_KEY) ?? ""
)
  .split(",")
  .map(Number);

function AnnouncementList() {
  const [announcementsRead, setAnnouncementsRead] = React.useState(
    initialAnnouncementsRead
  );

  /**
   * Mark an announcement read by id
   */
  const markAsRead = (id: number) => {
    // Get list of current announcement ids
    const currentIds = currentAnnouncements.map((cur) => cur.id);
    // Filter out any old values of deleted announcements from prior storage
    const filtered = announcementsRead.filter((old) =>
      currentIds.includes(old)
    );
    // Append this announcement to be marked as read
    const newAnnouncementsRead = [...filtered, id];

    // Store the ids as a comma-separated string in storage
    window.localStorage.setItem(
      ANNOUNCEMENT_KEY,
      newAnnouncementsRead.join(",")
    );
    // And update local state
    setAnnouncementsRead(newAnnouncementsRead);
  };

  // Make sure each current announcement hasn't been read and shouldn't be excluded
  const visibleAnnouncements = React.useMemo(
    () =>
      currentAnnouncements.filter(({ id }) => {
        const isRead = announcementsRead.includes(id);
        return !isRead;
      }),
    [announcementsRead]
  );

  return (
    <AnnouncementListWrapper>
      <Container width="lg">
        {visibleAnnouncements.map((announcement) => (
          <AnnouncementElement
            key={announcement.id}
            {...announcement}
            markAsRead={markAsRead}
          />
        ))}
      </Container>
    </AnnouncementListWrapper>
  );
}

const dismissDuration = 500;

type AnnouncementElementProps = Omit<Announcement, "exclude"> & {
  markAsRead: (id: number) => void;
};

function AnnouncementElement({
  id,
  link,
  markAsRead,
  message,
}: AnnouncementElementProps) {
  const [isHidden, setIsHidden] = React.useState(false);

  const hideAnnouncement = () => {
    // Start animation to hide element
    setIsHidden(true);
    // Then remove it from the list after timeout
    setTimeout(() => {
      markAsRead(id);
    }, dismissDuration);
  };

  const internalLink = link?.to;
  const externalLink = link?.href;

  return (
    <AnnouncementMask isHidden={isHidden}>
      <AnnouncementWrapper>
        <AnnouncementContent>
          <span style={{ marginRight: 6 }}>{message}</span>

          {internalLink && (
            <AnnouncementButton
              variant="primary"
              size="xxs"
              to={link.to}
              onClick={hideAnnouncement}
            >
              {link.text}
            </AnnouncementButton>
          )}
          {externalLink && (
            <AnnouncementButton
              variant="primary"
              size="xxs"
              href={link.href}
              target="_blank"
              onClick={hideAnnouncement}
            >
              {link.text}
            </AnnouncementButton>
          )}
        </AnnouncementContent>

        <Dismiss aria-label="close" onClick={hideAnnouncement}>
          <IconX />
        </Dismiss>
      </AnnouncementWrapper>
    </AnnouncementMask>
  );
}

const AnnouncementListWrapper = styled(FadeIn)({
  position: "relative",
  zIndex: 3,
});

const lineHeight = "1.2rem";
const vPadding = "10px";

const AnnouncementMask = styled.div<{
  isHidden: boolean;
}>(({ isHidden, theme }) => ({
  marginTop: 10,
  marginBottom: 3,
  maxHeight: 200,
  opacity: 1,
  overflow: "hidden",
  transition: `${dismissDuration}ms ease all`,
  [theme.breakpoints.tablet]: {
    maxHeight: "5rem",
    ...(isHidden && {
      maxHeight: 0,
    }),
  },
  ...(isHidden && {
    marginTop: 0,
    marginBottom: 0,
    maxHeight: 0,
    opacity: 0,
  }),
}));

const AnnouncementWrapper = styled.div(({ theme }) => ({
  borderRadius: theme.borderRadius.sm,
  backgroundColor: theme.colors.accent2,
  display: "flex",
  justifyContent: "center",
  color: theme.colors.bg,
  fontSize: theme.fontSizes.xs,
  paddingRight: "3rem",
  paddingLeft: "1rem",
  position: "relative",
}));

const AnnouncementContent = styled.div({
  lineHeight,
  paddingTop: vPadding,
  paddingBottom: vPadding,
  position: "relative",
});

const AnnouncementButton = styled(Link)({
  backgroundColor: "rgb(0, 0, 0, .33)",
  border: 0,
  flex: "0 0 auto",
  padding: "4px 1em 3px 1em",
  position: "static",
  "&::before": {
    content: "''",
    position: "absolute",
    inset: 0,
  },
});

const Dismiss = styled(Button)(({ theme }) => ({
  border: 0,
  color: theme.colors.white,
  opacity: 0.7,
  justifySelf: "flex-end",
  flex: "0 0 auto",
  position: "absolute",
  margin: 0,
  top: 0,
  right: 0,
  height: `calc((${vPadding} * 2) + ${lineHeight})`,
  width: `calc((${vPadding} * 2) + ${lineHeight})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover, &:focus-visible": {
    opacity: 1,
    color: theme.colors.white,
  },
}));

Dismiss.defaultProps = {
  size: "xxs",
  variant: "blank",
};

export { AnnouncementList };
